'use client';
import { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation'
import { cn } from "@/lib/utils";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuList,
  navigationMenuTriggerStyle
} from "@/components/ui/navigation-menu";
import {
  Home,
  Search,
  Menu,
  X,
  Tag,
  User,
  Bell
} from 'lucide-react';
import { Button } from './ui/button';

const NavBar = () => {
  const pathName = usePathname();
  const isActive = (path: string) => pathName === path;
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const NavItems = () => (
    <>
      <NavigationMenuItem>
        <a href="/" className={cn(
          navigationMenuTriggerStyle(),
          isActive('/') ? "bg-accent text-accent-foreground" : ""
        )}>
          <Home className="mr-2 h-4 w-4" />
          Home
        </a>
      </NavigationMenuItem>
      <NavigationMenuItem>
        <Link href="/search" className={cn(
          navigationMenuTriggerStyle(),
          isActive('/search') ? "bg-accent text-accent-foreground" : ""
        )}>
          <Search className="mr-2 h-4 w-4" />
          Search Quotes
        </Link>
      </NavigationMenuItem>
      <NavigationMenuItem>
        <Link href="/authors" className={cn(
          navigationMenuTriggerStyle(),
          isActive('/authors') ? "bg-accent text-accent-foreground" : ""
        )}>
          <User className="mr-2 h-4 w-4" />
          Authors
        </Link>
      </NavigationMenuItem>
      <NavigationMenuItem>
        <Link href="/categories" className={cn(
          navigationMenuTriggerStyle(),
          isActive('/categories') ? "bg-accent text-accent-foreground" : ""
        )}>
          <Tag className="mr-2 h-4 w-4" />
          Categories
        </Link>
      </NavigationMenuItem>
      <NavigationMenuItem>
        <Link href="/dailyquotes" className={cn(
          navigationMenuTriggerStyle(),
          isActive('/dailyquotes') ? "bg-accent text-accent-foreground" : ""
        )}>
          <Bell className="mr-2 h-4 w-4" />
          Daily Quotes
        </Link>
      </NavigationMenuItem>
    </>
  );

  return (
    <div className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 mt-0">
      <div className="container flex h-14 items-center relative">
        {/* Mobile Logo - Centered */}
        <div className="md:hidden absolute inset-0 flex items-center justify-center pointer-events-none">
          <Link href="/" className="inline-flex items-center pointer-events-auto">
            <span className="text-xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
              Inspiriva Quotes
            </span>
          </Link>
        </div>

        {/* Mobile Navigation - Moved to the right */}
        <div className="md:hidden relative ml-auto">
          <Button
            variant="ghost"
            size="icon"
            className="ml-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <Menu className="h-5 w-5" />
            <span className="sr-only">Toggle menu</span>
          </Button>

          {isMenuOpen && (
            <>
              {/* Backdrop */}
              <div
                className="fixed top-0 left-0 right-0 bottom-0 z-40 bg-black/20"
                onClick={() => setIsMenuOpen(false)}
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  width: '100vw',
                  height: '100vh',
                  minHeight: '100vh'
                }}
              />

              {/* Dropdown Menu - Positioned from the right */}
              <div className="absolute top-12 right-0 z-50 w-80 bg-background border rounded-lg shadow-lg mt-2 max-h-[calc(100vh-200px)] overflow-y-auto">
                <div className="p-4">
                  <div className="flex items-center justify-end mb-6">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <X className="h-4 w-4" />
                      <span className="sr-only">Close menu</span>
                    </Button>
                  </div>

                  <nav className="flex flex-col space-y-2">
                    <a
                      href="/"
                      className={cn(
                        "flex items-center gap-2 px-3 py-2 rounded-md hover:bg-accent transition-colors",
                        isActive('/') ? "bg-accent text-accent-foreground" : ""
                      )}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <Home className="h-4 w-4" />
                      <span>Home</span>
                    </a>
                    <Link
                      href="/search"
                      className={cn(
                        "flex items-center gap-2 px-3 py-2 rounded-md hover:bg-accent transition-colors",
                        isActive('/search') ? "bg-accent text-accent-foreground" : ""
                      )}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <Search className="h-4 w-4" />
                      <span>Search Quotes</span>
                    </Link>
                    <Link
                      href="/authors"
                      className={cn(
                        "flex items-center gap-2 px-3 py-2 rounded-md hover:bg-accent transition-colors",
                        isActive('/authors') ? "bg-accent text-accent-foreground" : ""
                      )}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <User className="h-4 w-4" />
                      <span>Authors</span>
                    </Link>
                    <Link
                      href="/categories"
                      className={cn(
                        "flex items-center gap-2 px-3 py-2 rounded-md hover:bg-accent transition-colors",
                        isActive('/categories') ? "bg-accent text-accent-foreground" : ""
                      )}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <Tag className="h-4 w-4" />
                      <span>Categories</span>
                    </Link>
                    <Link
                      href="/dailyquotes"
                      className={cn(
                        "flex items-center gap-2 px-3 py-2 rounded-md hover:bg-accent transition-colors",
                        isActive('/dailyquotes') ? "bg-accent text-accent-foreground" : ""
                      )}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <Bell className="h-4 w-4" />
                      <span>Daily Quotes</span>
                    </Link>
                  </nav>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Desktop Logo */}
        <div className="hidden md:flex ml-[56px]">
          <Link href="/" className="flex items-center space-x-2">
            <span className="text-xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
              Inspiriva Quotes
            </span>
          </Link>
        </div>

        {/* Desktop Navigation */}
        <div className="flex-1 hidden md:flex justify-center">
          <NavigationMenu>
            <NavigationMenuList>
              <NavItems />
            </NavigationMenuList>
          </NavigationMenu>
        </div>

        {/* Desktop Right Space - for balance */}
        <div className="hidden md:flex w-[200px]">
        </div>

      </div>
    </div>
  );
};

export default NavBar;
